import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { DenominationCalculator } from './DenominationCalculator';
import { DenominationData, DayEndClosure } from '@/app/types/dayEndClosure';
import { Loader2, AlertCircle, CheckCircle, IndianRupee } from 'lucide-react';
import dayEndClosureService, {
  ClosingBalancePreview,
  DiscrepancyPreview
} from '@/app/services/dayEndClosureService';
import { toast } from 'sonner';

interface CloseDayDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  closure: DayEndClosure;
  onSuccess: () => void;
}

export const CloseDayDialog: React.FC<CloseDayDialogProps> = ({
  open,
  onOpenChange,
  closure,
  onSuccess
}) => {
  const [step, setStep] = useState<'count' | 'review' | 'confirm'>('count');
  const [loading, setLoading] = useState(false);
  const [denominationData, setDenominationData] = useState<DenominationData>({});
  const [totalAmount, setTotalAmount] = useState(0);
  const [countNotes, setCountNotes] = useState('');
  const [adjustmentNotes, setAdjustmentNotes] = useState('');
  const [confirmDiscrepancy, setConfirmDiscrepancy] = useState(false);
  
  // Preview data
  const [closingPreview, setClosingPreview] = useState<ClosingBalancePreview | null>(null);
  const [discrepancyPreview, setDiscrepancyPreview] = useState<DiscrepancyPreview | null>(null);

  // Fetch closing balance preview when dialog opens
  useEffect(() => {
    const fetchClosingPreview = async () => {
      if (open && closure) {
        setLoading(true);
        try {
          const preview = await dayEndClosureService.previewClosingBalance(
            closure.site_id, 
            closure.business_date
          );
          setClosingPreview(preview);
        } catch (error) {
          toast.error('Failed to fetch closing balance preview');
          console.error(error);
        } finally {
          setLoading(false);
        }
      }
    };
    fetchClosingPreview();
  }, [open, closure]);

  // Calculate discrepancy preview when amount changes
  useEffect(() => {
    const calculateDiscrepancy = async () => {
      if (totalAmount > 0 && open && closure) {
        try {
          const preview = await dayEndClosureService.previewDiscrepancy(
            'closing',
            closure.site_id,
            denominationData,
            countNotes,
            closure.business_date
          );
          setDiscrepancyPreview(preview);
        } catch (error) {
          console.error('Failed to calculate discrepancy:', error);
        }
      } else {
        setDiscrepancyPreview(null);
      }
    };

    // Debounce the calculation
    const timer = setTimeout(calculateDiscrepancy, 500);
    return () => clearTimeout(timer);
  }, [totalAmount, denominationData, countNotes, closure, open]);

  const handleProceedToReview = () => {
    setStep('review');
  };

  const handleBackToCount = () => {
    setStep('count');
  };

  const handleCloseDayWithCount = async () => {
    setLoading(true);
    try {
      await dayEndClosureService.closeDayWithCount(
        closure.id,
        {
          denomination_data: denominationData,
          notes: countNotes || 'Closing count recorded'
        },
        confirmDiscrepancy,
        adjustmentNotes || countNotes || 'Day closed'
      );
      
      toast.success('Day closed successfully!');
      onSuccess();
      handleClose();
    } catch (error) {
      toast.error(dayEndClosureService.getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep('count');
    setDenominationData({});
    setTotalAmount(0);
    setCountNotes('');
    setAdjustmentNotes('');
    setConfirmDiscrepancy(false);
    setDiscrepancyPreview(null);
    onOpenChange(false);
  };

  const expectedAmount = closingPreview?.expected_closing_balance || 
                        parseFloat(closure?.closing_balance_software || '0');
  const hasDiscrepancy = discrepancyPreview?.discrepancy_type !== 'balanced';

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        {step === 'count' ? (
          <>
            <DialogHeader>
              <DialogTitle>Close Business Day</DialogTitle>
              <DialogDescription>
                Count the physical cash to close the business day for {new Date(closure.business_date).toLocaleDateString()}.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6">
              {/* Show expected balance upfront */}
              {closingPreview && (
                <Alert>
                  <IndianRupee className="h-4 w-4" />
                  <AlertDescription>
                    <div className="space-y-1">
                      <p className="font-medium">
                        Expected closing balance: ₹{expectedAmount.toLocaleString('en-IN')}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Based on opening balance + today's transactions
                      </p>
                      {closingPreview.today_movement !== 0 && (
                        <p className="text-sm text-muted-foreground">
                          Today's movement: ₹{Math.abs(closingPreview.today_movement).toLocaleString('en-IN')} 
                          {closingPreview.today_movement > 0 ? ' (increase)' : ' (decrease)'}
                        </p>
                      )}
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              <DenominationCalculator
                onTotalChange={(total, denoms) => {
                  setTotalAmount(total);
                  setDenominationData(denoms);
                }}
                expectedAmount={expectedAmount}
                title="Closing Cash Count"
              />

              {/* Live discrepancy preview */}
              {discrepancyPreview && (
                <Alert variant={
                  discrepancyPreview.discrepancy_type === 'shortage' ? 'destructive' : 
                  discrepancyPreview.discrepancy_type === 'overage' ? 'default' : 
                  'default'
                }>
                  {discrepancyPreview.discrepancy_type === 'balanced' ? (
                    <CheckCircle className="h-4 w-4" />
                  ) : (
                    <AlertCircle className="h-4 w-4" />
                  )}
                  <AlertDescription>
                    {discrepancyPreview.discrepancy_type === 'balanced' ? (
                      'Your count matches the expected balance perfectly!'
                    ) : (
                      <>
                        {discrepancyPreview.discrepancy_type === 'shortage' ? 'Shortage' : 'Overage'} 
                        {' '}of ₹{Math.abs(discrepancyPreview.discrepancy).toLocaleString('en-IN')} detected.
                        {discrepancyPreview.requires_adjustment && ' An adjustment voucher will be created.'}
                      </>
                    )}
                  </AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="countNotes">Notes (Optional)</Label>
                <Textarea
                  id="countNotes"
                  placeholder="Add any notes about the closing count..."
                  value={countNotes}
                  onChange={(e) => setCountNotes(e.target.value)}
                  rows={3}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>Cancel</Button>
              <Button 
                onClick={handleProceedToReview} 
                disabled={loading || totalAmount === 0}
              >
                Next: Review & Finalize
              </Button>
            </DialogFooter>
          </>
        ) : step === 'review' ? (
          <>
            <DialogHeader>
              <DialogTitle>Review Day Closure</DialogTitle>
              <DialogDescription>
                Review the day's reconciliation before final confirmation.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6">
              {/* Day Summary */}
              <div className="space-y-4 bg-gray-50 p-4 rounded-lg">
                <h3 className="font-medium">Day Summary</h3>
                
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Opening Balance (Software):</span>
                    <span>₹{parseFloat(closure.opening_balance_software).toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Opening Balance (Physical):</span>
                    <span>₹{parseFloat(closure.opening_balance_physical || '0').toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between font-medium">
                    <span>Opening Discrepancy:</span>
                    <span className={parseFloat(closure.opening_discrepancy || '0') < 0 ? 'text-destructive' : 'text-success'}>
                      ₹{Math.abs(parseFloat(closure.opening_discrepancy || '0')).toLocaleString('en-IN')}
                      {parseFloat(closure.opening_discrepancy || '0') < 0 ? ' (Shortage)' : 
                       parseFloat(closure.opening_discrepancy || '0') > 0 ? ' (Overage)' : ''}
                    </span>
                  </div>
                </div>

                <div className="border-t pt-2 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Expected Closing Balance:</span>
                    <span>₹{expectedAmount.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Your Count:</span>
                    <span className="font-medium">₹{totalAmount.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="border-t pt-2 flex justify-between font-medium">
                    <span>Closing Discrepancy:</span>
                    <span className={
                      !discrepancyPreview ? '' :
                      discrepancyPreview.discrepancy_type === 'shortage' ? 'text-destructive' : 
                      discrepancyPreview.discrepancy_type === 'overage' ? 'text-warning' : 
                      'text-success'
                    }>
                      {!discrepancyPreview ? 'Calculating...' :
                        discrepancyPreview.discrepancy_type === 'balanced' ? 'Balanced' :
                        `₹${Math.abs(discrepancyPreview.discrepancy).toLocaleString('en-IN')} ${
                          discrepancyPreview.discrepancy_type === 'shortage' ? 'Short' : 'Over'
                        }`
                      }
                    </span>
                  </div>
                </div>
              </div>

              {/* Discrepancy handling */}
              {hasDiscrepancy && discrepancyPreview && (
                <>
                  <Alert variant={discrepancyPreview.discrepancy_type === 'shortage' ? "destructive" : "default"}>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      A discrepancy of ₹{Math.abs(discrepancyPreview.discrepancy).toLocaleString('en-IN')} has been detected.
                      An adjustment voucher will be created to reconcile this difference.
                    </AlertDescription>
                  </Alert>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="confirmDiscrepancy"
                      checked={confirmDiscrepancy}
                      onCheckedChange={(checked) => setConfirmDiscrepancy(checked as boolean)}
                    />
                    <Label htmlFor="confirmDiscrepancy" className="text-sm">
                      I confirm the discrepancy and authorize the adjustment entry
                    </Label>
                  </div>
                </>
              )}

              <div className="space-y-2">
                <Label htmlFor="adjustmentNotes">
                  Adjustment Notes {hasDiscrepancy && '(Required)'}
                </Label>
                <Textarea
                  id="adjustmentNotes"
                  placeholder={hasDiscrepancy ? "Please explain the reason for discrepancy..." : "Add any closing notes..."}
                  value={adjustmentNotes}
                  onChange={(e) => setAdjustmentNotes(e.target.value)}
                  rows={3}
                  required={hasDiscrepancy}
                />
              </div>

              {/* What will happen */}
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Once you confirm:
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>Your closing count of ₹{totalAmount.toLocaleString('en-IN')} will be recorded</li>
                    <li>The business day will be closed</li>
                    {discrepancyPreview?.requires_adjustment && (
                      <li>An adjustment voucher will be created for the {discrepancyPreview.discrepancy_type}</li>
                    )}
                  </ul>
                </AlertDescription>
              </Alert>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleBackToCount}>Back</Button>
              <Button 
                onClick={() => setStep('confirm')} 
                disabled={loading || (hasDiscrepancy && (!confirmDiscrepancy || !adjustmentNotes.trim()))}
              >
                Proceed to Final Confirmation
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Final Confirmation</DialogTitle>
              <DialogDescription>
                Please confirm all details before closing the business day.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6">
              {/* Final Summary Box */}
              <div className="bg-primary/5 border border-primary/30 p-4 rounded-lg space-y-4">
                <h3 className="font-semibold text-lg">Day Closure Summary</h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Business Date</p>
                    <p className="text-lg font-medium">{new Date(closure.business_date).toLocaleDateString('en-IN', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Total Transactions</p>
                    <p className="text-lg font-medium">
                      {closingPreview?.today_movement > 0 ? '+' : ''}
                      ₹{Math.abs(closingPreview?.today_movement || 0).toLocaleString('en-IN')}
                    </p>
                  </div>
                </div>

                <div className="border-t pt-3 grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Expected Closing</p>
                    <p className="text-lg font-medium">₹{expectedAmount.toLocaleString('en-IN')}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Your Count</p>
                    <p className="text-lg font-medium">₹{totalAmount.toLocaleString('en-IN')}</p>
                  </div>
                </div>

                {discrepancyPreview && (
                  <div className="border-t pt-3">
                    <p className="text-sm text-gray-600">Closing Status</p>
                    <p className={
                      discrepancyPreview.discrepancy_type === 'balanced' 
                        ? 'text-success font-medium text-lg' 
                        : discrepancyPreview.discrepancy_type === 'shortage' 
                        ? 'text-destructive font-medium text-lg' 
                        : 'text-warning font-medium text-lg'
                    }>
                      {discrepancyPreview.discrepancy_type === 'balanced' 
                        ? '✓ Perfectly Balanced'
                        : `⚠ ${discrepancyPreview.discrepancy_type === 'shortage' ? 'Cash Shortage' : 'Cash Overage'}: ₹${Math.abs(discrepancyPreview.discrepancy).toLocaleString('en-IN')}`
                      }
                    </p>
                  </div>
                )}
              </div>

              {/* Actions to be taken */}
              <Alert className="border-warning/30 bg-warning/5">
                <AlertCircle className="h-4 w-4 text-warning" />
                <AlertDescription className="text-warning">
                  <p className="font-medium mb-2">The following actions will be taken:</p>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    <li>The business day for {new Date(closure.business_date).toLocaleDateString()} will be closed</li>
                    <li>Closing count of ₹{totalAmount.toLocaleString('en-IN')} will be recorded</li>
                    {discrepancyPreview?.requires_adjustment && (
                      <li className="font-medium">
                        An adjustment voucher for ₹{Math.abs(discrepancyPreview.discrepancy).toLocaleString('en-IN')} 
                        {discrepancyPreview.discrepancy_type === 'shortage' ? ' shortage' : ' overage'} will be created
                      </li>
                    )}
                    <li>No further transactions can be recorded for this day</li>
                    {adjustmentNotes && <li>Your notes: "{adjustmentNotes}"</li>}
                  </ul>
                </AlertDescription>
              </Alert>

              {/* Day Summary Stats */}
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <h4 className="font-medium text-sm text-gray-700">Day Summary</h4>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Opening</p>
                    <p className="font-medium">₹{parseFloat(closure.opening_balance_physical || '0').toLocaleString('en-IN')}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Movement</p>
                    <p className="font-medium">
                      {closingPreview?.today_movement && closingPreview.today_movement > 0 ? '+' : ''}
                      ₹{Math.abs(closingPreview?.today_movement || 0).toLocaleString('en-IN')}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600">Closing</p>
                    <p className="font-medium">₹{totalAmount.toLocaleString('en-IN')}</p>
                  </div>
                </div>
              </div>

              {/* Final confirmation statement */}
              <div className="bg-destructive/5 border border-destructive/30 p-4 rounded-lg">
                <p className="text-sm text-destructive font-medium">
                  ⚠ This action cannot be undone. Once the day is closed, you cannot record any more transactions for this date.
                </p>
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setStep('review')}>Back to Review</Button>
              <Button 
                onClick={handleCloseDayWithCount} 
                disabled={loading}
                className="bg-destructive hover:bg-destructive/90 text-white"
              >
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Confirm & Close Day
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};